import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import { StatusBadge } from "components/ui/StatusBadge";
import {
  useSlackChannels,
  useSlackIntegration,
} from "hooks/use-slack-integration-api";
import {
  type TalentNotifyRuleCategory,
  type TalentNotifyRuleItem,
  type TalentNotifyRules,
  usePatchTalentNotifyRule,
  useResetTalentNotifyRules,
  useTalentNotifyRules,
  useTestTalentNotification,
  useUpdateTalentNotifyRules,
} from "hooks/use-talent-notify-rules-api";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { formatDateTime } from "utils/relative-time";

type Props = {
  talentId: string;
  canEdit: boolean;
};

const CATEGORY_LABELS: Record<TalentNotifyRuleCategory, string> = {
  pipeline: "営業進捗",
  contract: "契約",
  matter: "案件",
  talent: "タレント",
};

const CATEGORY_ORDER: TalentNotifyRuleCategory[] = [
  "pipeline",
  "contract",
  "matter",
  "talent",
];

/**
 * タレント詳細画面の通知設定タブ
 *
 * 機能:
 * - 通知先（Slackチャンネル・通知メール）の表示と編集
 * - 16種類の通知イベントをカテゴリ別アコーディオンで表示
 * - 各イベントの Slack/メール/有効 トグル
 * - テスト通知送信
 * - デフォルトにリセット
 * - Slack未連携時のフォールバック表示
 * - 通知先未設定時のセットアップガイド
 *
 * @param {object} props プロパティ
 * @param {string} props.talentId タレントID
 * @param {boolean} props.canEdit 編集権限の有無
 * @return {React.ReactElement} 通知設定タブ
 */
export const TalentNotifySettingsTab: React.FC<Props> = ({
  talentId,
  canEdit,
}) => {
  const { data, loading, error, refetch } = useTalentNotifyRules(talentId);
  const { integration: slackIntegration } = useSlackIntegration();
  const { channels, fetchChannels } = useSlackChannels();
  const { update, loading: updating } = useUpdateTalentNotifyRules();
  const { patchRule } = usePatchTalentNotifyRule();
  const { reset, loading: resetting } = useResetTalentNotifyRules();
  const { sendTest, loading: testing } = useTestTalentNotification();

  const [editingDestinations, setEditingDestinations] = useState(false);
  const [draftChannelId, setDraftChannelId] = useState<string>("");
  const [draftEmail, setDraftEmail] = useState<string>("");
  const [openCategories, setOpenCategories] = useState<
    Set<TalentNotifyRuleCategory>
  >(new Set(["pipeline"]));

  useEffect(() => {
    if (slackIntegration?.isConnected) {
      fetchChannels().catch(() => undefined);
    }
  }, [slackIntegration?.isConnected, fetchChannels]);

  /**
   * 編集モードに入る。draftは編集開始時点のサーバ値で初期化する。
   * useEffect での mirror は使わない（refetchが編集中の入力を上書きしないように）。
   * @return {void}
   */
  const handleStartEditing = useCallback((): void => {
    if (data) {
      setDraftChannelId(data.slackChannelId ?? "");
      setDraftEmail(data.notificationEmail ?? "");
    }
    setEditingDestinations(true);
  }, [data]);

  const groupedRules = useMemo(() => {
    if (!data)
      return {} as Record<TalentNotifyRuleCategory, TalentNotifyRuleItem[]>;
    const grouped: Record<TalentNotifyRuleCategory, TalentNotifyRuleItem[]> = {
      pipeline: [],
      contract: [],
      matter: [],
      talent: [],
    };
    for (const rule of data.rules) {
      grouped[rule.category]?.push(rule);
    }
    return grouped;
  }, [data]);

  /**
   * カテゴリの開閉
   * @param {string} category カテゴリ名
   * @return {void}
   */
  const toggleCategory = useCallback(
    (category: TalentNotifyRuleCategory): void => {
      setOpenCategories(prev => {
        const next = new Set(prev);
        if (next.has(category)) {
          next.delete(category);
        } else {
          next.add(category);
        }
        return next;
      });
    },
    [],
  );

  /**
   * 個別ルールのフィールドをトグル
   * @param {object} rule 既存ルール
   * @param {string} field トグル対象のフィールド
   * @return {Promise<void>}
   */
  const handleToggleField = useCallback(
    async (
      rule: TalentNotifyRuleItem,
      field: "slackEnabled" | "emailEnabled" | "isEnabled",
    ): Promise<void> => {
      if (!canEdit) return;
      try {
        await patchRule(talentId, rule.eventType, {
          [field]: !rule[field],
        });
        await refetch();
      } catch {
        toast.error("通知ルールの更新に失敗しました");
      }
    },
    [canEdit, patchRule, refetch, talentId],
  );

  /**
   * 通知先の保存
   * @return {Promise<void>}
   */
  const handleSaveDestinations = useCallback(async (): Promise<void> => {
    const channelName = channels.find(c => c.id === draftChannelId)?.name;
    try {
      await update(talentId, {
        updateDestinations: true,
        slackChannelId: draftChannelId || null,
        slackChannelName: channelName ? `#${channelName}` : null,
        notificationEmail: draftEmail || null,
        rules: [],
      });
      toast.success("通知先を更新しました");
      setEditingDestinations(false);
      await refetch();
    } catch {
      toast.error("通知先の更新に失敗しました");
    }
  }, [channels, draftChannelId, draftEmail, refetch, talentId, update]);

  /**
   * デフォルトにリセット
   * @return {Promise<void>}
   */
  const handleReset = useCallback(async (): Promise<void> => {
    if (
      !window.confirm(
        "現在の設定をすべてデフォルトに戻しますか？この操作は取り消せません。",
      )
    ) {
      return;
    }
    try {
      await reset(talentId);
      toast.success("通知ルールをデフォルトにリセットしました");
      await refetch();
    } catch {
      toast.error("リセットに失敗しました");
    }
  }, [refetch, reset, talentId]);

  /**
   * テスト通知の送信
   * @param {string} channel 送信チャネル
   * @return {Promise<void>}
   */
  const handleSendTest = useCallback(
    async (channel: "slack" | "email"): Promise<void> => {
      try {
        const result = await sendTest(talentId, channel);
        if (result.success) {
          toast.success(
            channel === "slack"
              ? "Slackにテスト通知を送信しました"
              : "メールにテスト通知を送信しました",
          );
        } else {
          toast.error(result.errorMessage ?? "テスト送信に失敗しました");
        }
      } catch {
        toast.error("テスト送信に失敗しました");
      }
    },
    [sendTest, talentId],
  );

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loading size="md" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="alert alert-error">
        <span>通知設定の取得に失敗しました</span>
      </div>
    );
  }

  const hasDestinations = Boolean(
    data.slackChannelId || data.notificationEmail,
  );
  const showSetupGuide = !hasDestinations && !editingDestinations;

  return (
    <div className="space-y-4">
      {/* 通知先カード */}
      <DestinationCard
        data={data}
        editing={editingDestinations}
        canEdit={canEdit}
        draftChannelId={draftChannelId}
        draftEmail={draftEmail}
        onChangeDraftChannelId={setDraftChannelId}
        onChangeDraftEmail={setDraftEmail}
        onStartEditing={handleStartEditing}
        onCancel={() => setEditingDestinations(false)}
        onSave={handleSaveDestinations}
        onSendTest={handleSendTest}
        saving={updating}
        testing={testing}
        slackChannels={channels}
      />

      {/* セットアップガイド or ルールテーブル */}
      {showSetupGuide ? (
        <SetupGuide
          slackConnected={data.slackConnected}
          canEdit={canEdit}
          onStartEditing={handleStartEditing}
        />
      ) : (
        <RulesSection
          groupedRules={groupedRules}
          openCategories={openCategories}
          onToggleCategory={toggleCategory}
          onToggleField={handleToggleField}
          canEdit={canEdit}
          onReset={handleReset}
          resetting={resetting}
          slackConnected={data.slackConnected}
        />
      )}
    </div>
  );
};

// ================================================================
// 通知先カード
// ================================================================

type DestinationCardProps = {
  data: TalentNotifyRules;
  editing: boolean;
  canEdit: boolean;
  draftChannelId: string;
  draftEmail: string;
  onChangeDraftChannelId: (value: string) => void;
  onChangeDraftEmail: (value: string) => void;
  onStartEditing: () => void;
  onCancel: () => void;
  onSave: () => void;
  onSendTest: (channel: "slack" | "email") => void;
  saving: boolean;
  testing: boolean;
  slackChannels: Array<{ id: string; name: string; isPrivate: boolean }>;
};

/**
 * 通知先カード（Slackチャンネル + 通知メール）
 * @param {object} props プロパティ
 * @return {React.ReactElement} カード
 */
const DestinationCard: React.FC<DestinationCardProps> = ({
  data,
  editing,
  canEdit,
  draftChannelId,
  draftEmail,
  onChangeDraftChannelId,
  onChangeDraftEmail,
  onStartEditing,
  onCancel,
  onSave,
  onSendTest,
  saving,
  testing,
  slackChannels,
}) => {
  const emailSuggestion =
    !editing && !data.notificationEmail
      ? data.partnerContact || data.talentEmail || null
      : null;

  return (
    <div className="card bg-base-100 shadow-sm border border-base-200">
      <div className="card-body p-5">
        <div className="flex items-center justify-between mb-2">
          <h2 className="card-title text-base">通知先</h2>
          {data.slackConnected ? (
            <StatusBadge tone="soft-success">Slack連携済み</StatusBadge>
          ) : (
            <StatusBadge tone="soft-warning">Slack未接続</StatusBadge>
          )}
        </div>

        {!data.slackConnected && (
          <div className="alert alert-warning text-xs py-2 mb-2">
            <span>
              ワークスペースのSlack連携を設定すると、タレントごとのSlack通知が利用できます。
            </span>
            <Button asChild variant="ghost" size="xs">
              <Link to="/settings/slack">Slack連携設定へ →</Link>
            </Button>
          </div>
        )}

        {data.emailBounceAt && (
          <div className="alert alert-error text-xs py-2 mb-2">
            <span>
              ⚠️ メールアドレス「{data.notificationEmail}
              」がバウンスしたため、メール通知を自動的にOFFにしました（
              {formatDateTime(data.emailBounceAt)}）。
              アドレスを更新するか、無効なアドレスの場合は通知先メールを変更してください。
            </span>
          </div>
        )}

        {editing ? (
          <div className="space-y-3">
            <div>
              <label
                htmlFor="talent-notify-slack-channel"
                className="label label-text text-xs"
              >
                Slackチャンネル
              </label>
              <select
                id="talent-notify-slack-channel"
                className="select select-bordered select-sm w-full"
                value={draftChannelId}
                onChange={e => onChangeDraftChannelId(e.target.value)}
                disabled={!data.slackConnected}
              >
                <option value="">（チャンネル未設定）</option>
                {slackChannels.map(c => (
                  <option key={c.id} value={c.id}>
                    #{c.name}
                    {c.isPrivate ? " 🔒" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="talent-notify-email"
                className="label label-text text-xs"
              >
                通知先メールアドレス
              </label>
              <input
                id="talent-notify-email"
                type="email"
                className="input input-bordered input-sm w-full"
                placeholder="例: partner@example.com"
                value={draftEmail}
                onChange={e => onChangeDraftEmail(e.target.value)}
              />
              <p className="text-xs text-base-content/60 mt-1">
                タレント本人のメール（{data.talentEmail ?? "未登録"}
                ）とは別に、通知専用のアドレスを設定できます。
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                onClick={onSave}
                disabled={saving}
              >
                {saving ? "保存中…" : "保存"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onCancel}
              >
                キャンセル
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-base-content/60 w-32">Slackチャンネル</span>
              <span>{data.slackChannelName ?? "未設定"}</span>
              {data.slackChannelId && (
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={() => onSendTest("slack")}
                  disabled={testing}
                >
                  テスト送信
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-base-content/60 w-32">通知先メール</span>
              <span>{data.notificationEmail ?? "未設定"}</span>
              {data.notificationEmail && (
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={() => onSendTest("email")}
                  disabled={testing}
                >
                  テスト送信
                </Button>
              )}
            </div>
            {emailSuggestion && (
              <div className="text-xs text-base-content/60">
                ℹ️ サジェスト: {emailSuggestion}
              </div>
            )}
            {canEdit && (
              <div className="mt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onStartEditing}
                >
                  通知先を編集
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ================================================================
// セットアップガイド（空状態）
// ================================================================

type SetupGuideProps = {
  slackConnected: boolean;
  canEdit: boolean;
  onStartEditing: () => void;
};

/**
 * 通知先未設定時のセットアップガイド
 * @param {object} props プロパティ
 * @return {React.ReactElement} ガイド
 */
const SetupGuide: React.FC<SetupGuideProps> = ({
  slackConnected,
  canEdit,
  onStartEditing,
}) => {
  return (
    <div className="card bg-base-100 shadow-sm border border-dashed border-base-300">
      <div className="card-body items-center text-center py-10">
        <div className="text-4xl mb-2">📬</div>
        <h3 className="card-title text-base">
          通知先を設定して、重要なイベントを見逃さない
        </h3>
        <p className="text-sm text-base-content/70 max-w-md">
          このタレントに関する営業進捗や契約の変更を、Slackチャンネルやメールで自動通知できます。
        </p>
        <ol className="text-xs text-base-content/60 list-decimal list-inside text-left mt-2">
          <li>
            Slackチャンネル
            {!slackConnected && "（要連携）"}またはメールアドレスを設定
          </li>
          <li>通知したいイベントを選択</li>
        </ol>
        {canEdit && (
          <div className="mt-3">
            <Button type="button" size="sm" onClick={onStartEditing}>
              通知先を設定
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ================================================================
// ルールセクション（カテゴリ別）
// ================================================================

type RulesSectionProps = {
  groupedRules: Record<TalentNotifyRuleCategory, TalentNotifyRuleItem[]>;
  openCategories: Set<TalentNotifyRuleCategory>;
  onToggleCategory: (category: TalentNotifyRuleCategory) => void;
  onToggleField: (
    rule: TalentNotifyRuleItem,
    field: "slackEnabled" | "emailEnabled" | "isEnabled",
  ) => void;
  canEdit: boolean;
  onReset: () => void;
  resetting: boolean;
  slackConnected: boolean;
};

/**
 * カテゴリ別アコーディオンのルール表
 * @param {object} props プロパティ
 * @return {React.ReactElement} セクション
 */
const RulesSection: React.FC<RulesSectionProps> = ({
  groupedRules,
  openCategories,
  onToggleCategory,
  onToggleField,
  canEdit,
  onReset,
  resetting,
  slackConnected,
}) => {
  return (
    <div className="card bg-base-100 shadow-sm border border-base-200">
      <div className="card-body p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="card-title text-base">通知ルール</h2>
          {canEdit && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onReset}
              disabled={resetting}
            >
              {resetting ? "リセット中…" : "デフォルトに戻す"}
            </Button>
          )}
        </div>

        <div className="space-y-2">
          {CATEGORY_ORDER.map(category => {
            const rules = groupedRules[category] ?? [];
            if (rules.length === 0) return null;
            const enabledCount = rules.filter(r => r.isEnabled).length;
            const isOpen = openCategories.has(category);
            return (
              <div
                key={category}
                className="border border-base-200 rounded-lg overflow-hidden"
              >
                <button
                  type="button"
                  className="flex items-center justify-between w-full px-4 py-2 bg-base-50 hover:bg-base-100"
                  onClick={() => onToggleCategory(category)}
                >
                  <span className="font-medium text-sm">
                    {isOpen ? "▼" : "▶"} {CATEGORY_LABELS[category]}
                  </span>
                  <span className="text-xs text-base-content/60">
                    {rules.length}件中 {enabledCount}件 有効
                  </span>
                </button>
                {isOpen && (
                  <RuleTable
                    rules={rules}
                    onToggleField={onToggleField}
                    canEdit={canEdit}
                    slackConnected={slackConnected}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

type RuleTableProps = {
  rules: TalentNotifyRuleItem[];
  onToggleField: (
    rule: TalentNotifyRuleItem,
    field: "slackEnabled" | "emailEnabled" | "isEnabled",
  ) => void;
  canEdit: boolean;
  slackConnected: boolean;
};

/**
 * ルール表（PCはtable、モバイルはカード形式）
 * @param {object} props プロパティ
 * @return {React.ReactElement} ルール表
 */
const RuleTable: React.FC<RuleTableProps> = ({
  rules,
  onToggleField,
  canEdit,
  slackConnected,
}) => {
  const hasUnsupported = rules.some(r => !r.layer2Supported);

  return (
    <div>
      {/* PC: テーブル表示 */}
      <div className="hidden md:block overflow-x-auto">
        <table className="table table-sm">
          <thead>
            <tr>
              <th>イベント</th>
              <th className="text-center">Slack</th>
              <th className="text-center">メール</th>
              <th className="text-center">有効</th>
            </tr>
          </thead>
          <tbody>
            {rules.map(rule => {
              const disabled = !canEdit || !rule.layer2Supported;
              return (
                <tr
                  key={rule.eventType}
                  className={!rule.layer2Supported ? "opacity-50" : ""}
                >
                  <td>{rule.label}</td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      className="toggle toggle-xs toggle-primary"
                      checked={rule.slackEnabled}
                      disabled={disabled || !slackConnected}
                      onChange={() => onToggleField(rule, "slackEnabled")}
                    />
                  </td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      className="toggle toggle-xs toggle-primary"
                      checked={rule.emailEnabled}
                      disabled={disabled}
                      onChange={() => onToggleField(rule, "emailEnabled")}
                    />
                  </td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      className="toggle toggle-xs"
                      checked={rule.isEnabled}
                      disabled={disabled}
                      onChange={() => onToggleField(rule, "isEnabled")}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* モバイル: カード表示 */}
      <div className="md:hidden p-2 space-y-2">
        {rules.map(rule => {
          const disabled = !canEdit || !rule.layer2Supported;
          return (
            <div
              key={rule.eventType}
              className={`border border-base-200 rounded p-2 ${
                !rule.layer2Supported ? "opacity-50" : ""
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{rule.label}</span>
                <label className="flex items-center gap-1 text-xs">
                  有効
                  <input
                    type="checkbox"
                    className="toggle toggle-xs"
                    checked={rule.isEnabled}
                    disabled={disabled}
                    onChange={() => onToggleField(rule, "isEnabled")}
                  />
                </label>
              </div>
              <div className="flex gap-3 text-xs">
                <label className="flex items-center gap-1">
                  Slack
                  <input
                    type="checkbox"
                    className="toggle toggle-xs toggle-primary"
                    checked={rule.slackEnabled}
                    disabled={disabled || !slackConnected}
                    onChange={() => onToggleField(rule, "slackEnabled")}
                  />
                </label>
                <label className="flex items-center gap-1">
                  メール
                  <input
                    type="checkbox"
                    className="toggle toggle-xs toggle-primary"
                    checked={rule.emailEnabled}
                    disabled={disabled}
                    onChange={() => onToggleField(rule, "emailEnabled")}
                  />
                </label>
              </div>
            </div>
          );
        })}
      </div>

      {hasUnsupported && (
        <p className="text-xs text-base-content/60 px-3 py-2">
          ℹ️
          一部イベントはワークスペース通知のみで配信されます（タレント個別設定の対象外）
        </p>
      )}
    </div>
  );
};
